// src/pages/ForgotPassword.jsx
import React, { useState } from 'react';
import { TrendingUp, Mail, ArrowRight, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

export default function ForgotPassword({ onBackToLogin }) {
  const { forgotPassword, loading, error } = useAuth();
  
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  
  // Local UI states for messages
  const [localError, setLocalError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');
    
    if (!email) {
      setLocalError('Please enter your email address');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setLocalError('Please enter a valid email address');
      return;
    }

    const result = await forgotPassword(email);
    
    if (result.success) {
      setEmailSent(true);
    } else {
      setLocalError(result.error || 'Failed to send reset email. Please try again.');
    }
  };

  // Success Screen - Email Sent
  if (emailSent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden hidden sm:block">
          <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-72 h-72 bg-teal-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse delay-1000"></div>
        </div>

        {/* Success Card */}
        <div className="relative w-full max-w-md">
          <div className="bg-gray-800/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-700/50 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-br from-emerald-600 to-teal-700 p-4 sm:p-8 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-white bg-opacity-20 rounded-xl sm:rounded-2xl mb-2 backdrop-blur-sm">
                <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
              </div>
              <h1 className="text-lg sm:text-3xl font-bold text-white mb-1">Check Your Email</h1>
              <p className="text-xs sm:text-sm text-emerald-100">
                We've sent password reset instructions
              </p>
            </div>

            {/* Content */}
            <div className="p-4 sm:p-8">
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 mb-6">
                <p className="text-emerald-300 text-sm text-center">
                  We've sent a password reset link to <span className="font-semibold">{email}</span>
                </p>
              </div>

              <div className="space-y-4 text-gray-300 text-sm">
                <p>
                  Please check your email and click on the password reset link to create a new password.
                </p>
                <p>
                  The link will expire in <span className="text-white font-semibold">1 hour</span> for security reasons.
                </p>
                <div className="bg-gray-700/50 rounded-xl p-4 border border-gray-600/50">
                  <p className="font-semibold mb-2 text-white">Didn't receive the email?</p>
                  <ul className="space-y-1 text-xs text-gray-400 list-disc list-inside">
                    <li>Check your spam or junk folder</li>
                    <li>Make sure you entered the correct email</li>
                    <li>Wait a few minutes and check again</li>
                  </ul>
                </div>
              </div>

              {/* Back to Login */}
              <button
                onClick={onBackToLogin}
                className="w-full mt-6 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-semibold rounded-xl px-6 py-3 flex items-center justify-center gap-2 hover:from-emerald-600 hover:to-teal-700 transition-all duration-300 active:scale-[0.98] shadow-lg shadow-emerald-500/20"
              >
                Back to Login
                <ArrowRight className="w-5 h-5" />
              </button>

              {/* Resend Option */}
              <div className="mt-4 text-center">
                <button
                  onClick={() => setEmailSent(false)}
                  className="text-emerald-400 hover:text-emerald-300 transition-colors text-sm font-medium"
                >
                  Try a different email address
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Email Input Screen
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden hidden sm:block">
        <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-72 h-72 bg-teal-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-indigo-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse delay-500"></div>
      </div>

      {/* Forgot Password Card */}
      <div className="relative w-full max-w-md">
        <div className="bg-gray-800/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-700/50 overflow-hidden">
          {/* Header Section */}
          <div className="bg-gradient-to-br from-emerald-600 to-teal-700 p-4 sm:p-8 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-white bg-opacity-20 rounded-xl sm:rounded-2xl mb-2 backdrop-blur-sm">
              <TrendingUp className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
            </div>
            <h1 className="text-lg sm:text-3xl font-bold text-white mb-1">Forgot Password?</h1>
            <p className="text-xs sm:text-sm text-emerald-100">
              No worries, we'll send you reset instructions
            </p>
          </div>

          {/* Form Section */}
          <div className="p-4 sm:p-8">
            {/* Error Message */}
            {(error || localError) && (
              <div className="mb-4 bg-red-500/10 border border-red-500/30 rounded-xl p-3 flex items-start gap-3 animate-in fade-in slide-in-from-top-2 duration-300">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-red-300 text-sm flex-1">{localError || error}</p>
              </div>
            )}

            <div className="space-y-4">
              {/* Email Input */}
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="w-5 h-5 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (localError) setLocalError('');
                    }}
                    placeholder="you@example.com"
                    disabled={loading}
                    className="w-full bg-gray-700/50 text-white placeholder-gray-400 rounded-xl pl-10 pr-4 py-2.5 sm:py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all disabled:opacity-50 border border-gray-600/50"
                  />
                </div>
                <p className="text-gray-400 text-xs mt-2">
                  Enter the email address associated with your account
                </p>
              </div>

              {/* Submit Button */}
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-semibold rounded-xl px-6 py-3 flex items-center justify-center gap-2 hover:from-emerald-600 hover:to-teal-700 transition-all duration-300 active:scale-[0.98] shadow-lg shadow-emerald-500/20 disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100"
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
                {!loading && <ArrowRight className="w-5 h-5" />}
              </button>
            </div>

            {/* Back to Login Link */}
            <div className="mt-6 text-center">
              <button
                onClick={onBackToLogin}
                disabled={loading}
                className="text-gray-400 hover:text-white transition-colors text-sm inline-flex items-center gap-2 disabled:opacity-50"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Login
              </button>
            </div>
          </div>
        </div>

        {/* Footer Note */}
        <p className="text-center text-gray-500 text-xs mt-4 px-4">
          Protected by industry-standard encryption and security measures
        </p>
      </div>
    </div>
  );
}